import { memo, useCallback, useMemo } from 'react';

import { Box } from '@mui/material';

import IndeterminateCheckbox from '@/components/IndeterminateCheckbox';
import type { SymbolKey } from '@/entities/symbol';
import { isSameSymbol, isSameSymbolIgnoringPeriod } from '@/entities/symbol';
import FactsheetDialogButton from '@/features/Factsheet/components/FactsheetDialogButton';
import {
  addSymbols,
  deleteSymbols,
  toggleSymbol
} from '@/features/SymbolSelector/store/symbolSelector.slice';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import useCatalogTableCell from '@/pages/Catalog/CatalogTable/CatalogTableRow/useCatalogTableCell';
import type { CatalogItem } from '@/pages/Catalog/types';

import type { Cell } from '@tanstack/react-table';

const ForwardCatalogCell = ({
  cell,
  symbols,
  isRestrictedData,
  isInDashboard
}: {
  cell: Cell<CatalogItem, unknown>;
  symbols: SymbolKey[];
  isRestrictedData: boolean;
  isInDashboard: boolean;
}) => {
  const dispatch = useAppDispatch();
  const isForwardMode = useAppSelector(state => state.symbolSelector.isForwardMode);
  const selectedSymbols = useAppSelector(state => state.symbolSelector.symbols);
  const columnId = cell.column.id;
  const cellValue = cell.getValue() as string;
  const { renderContent, defaultStyle } = useCatalogTableCell(columnId, cell);

  const { firstSelected, secondSelected } = useMemo(() => {
    const equals = isForwardMode ? isSameSymbolIgnoringPeriod : isSameSymbol;
    if (!symbols?.length) return { firstSelected: false, secondSelected: false };
    let any = false;
    let all = true;
    for (const sym of symbols) {
      let found = false;
      for (const s of selectedSymbols) {
        if (equals(s, sym)) {
          found = true;
          break;
        }
      }
      if (found) any = true;
      else all = false;
    }
    return { firstSelected: any, secondSelected: all };
  }, [isForwardMode, selectedSymbols, symbols]);

  const onChange = useCallback(() => {
    if (!symbols?.length) return;
    if (isForwardMode) {
      dispatch(toggleSymbol({ symbol: symbols[0] }));
    } else if (firstSelected) {
      dispatch(deleteSymbols({ symbols }));
    } else {
      dispatch(addSymbols({ symbols }));
    }
  }, [dispatch, isForwardMode, symbols, firstSelected]);

  const renderCellContent = () => {
    switch (columnId) {
      case 'Symbol': {
        return (
          <IndeterminateCheckbox
            disabled={isRestrictedData}
            firstSelected={firstSelected}
            secondSelected={secondSelected}
            onChange={onChange}
          />
        );
      }
      case 'Index':
        return <span>{cellValue === 'Y' ? 'Index' : 'High, Mid, Low'}</span>;
      case 'Periods':
        return <span>{cellValue?.toString().padStart(2, '0')}</span>;
      case 'Factsheet':
        return (
          <Box
            display='flex'
            justifyContent='center'
            width='100%'
          >
            <FactsheetDialogButton
              code={cell.row.getValue('Code')}
              mode={isInDashboard ? 'newTab' : 'modal'}
            />
          </Box>
        );
      default:
        return <>{renderContent()}</>;
    }
  };

  return (
    <div
      key={cell.id + columnId}
      style={defaultStyle}
    >
      {renderCellContent()}
    </div>
  );
};

export default memo(ForwardCatalogCell);
